import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ssoLogin } from '@/lib/api/user';
import { setToken } from '@/lib/auth';
import { safeToast } from '@/lib/toast';
import { useI18n } from '@/hooks/useI18n';
import styled from 'styled-components';
import { handleLoginRedirect } from '@/lib/loginRedirect';

const SsoLoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  padding: 20px;
`;

const LoadingCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 40px;
  text-align: center;
  // box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  max-width: 400px;
  width: 100%;
`;

const LoadingText = styled.div`
  margin-top: 20px;
  font-size: 16px;
  color: #666;
  font-weight: 500;
`;

const ErrorText = styled.div`
  margin-top: 20px;
  font-size: 16px;
  color: #ff4757;
  font-weight: 500;
`;

const RetryButton = styled.button`
  margin-top: 20px;
  padding: 12px 24px;
  // background: #667eea;
  color: blue;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.3s ease;

  &:hover {
    background: #5a6fd8;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const LoaderWrapper = styled.div`
  .loader {
    height: 120px;
    width: 120px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    margin: 0 auto;
  }

  .loader div {
    height: 30px;
    width: 30px;
    position: absolute;
    animation: move 4s infinite;
  }

  .loader div:nth-child(1) {
    background-color: rgb(158, 136, 246);
    box-shadow: rgb(158, 136, 246) 0px 7px 29px 0px;
    transform: translate(-30px,-30px);
    animation-delay: -1s;
  }

  .loader div:nth-child(2) {
    background-color: rgb(97, 183, 253);
    box-shadow: rgb(97, 183, 253) 0px 7px 29px 0px;
    transform: translate(30px,-30px);
    animation-delay: -2s;
  }

  .loader div:nth-child(3) {
    background-color: rgb(95, 249, 175);
    box-shadow: rgb(95, 249, 175) 0px 7px 29px 0px;
    transform: translate(30px,30px);
    animation-delay: -3s;
  }

  .loader div:nth-child(4) {
    background-color: rgb(243, 171, 89);
    box-shadow: rgb(243, 171, 89) 0px 7px 29px 0px;
    transform: translate(-30px,30px);
    animation-delay: -4s;
  }

  @keyframes move {
    0% {
      transform: translate(-30px, -30px);
    }
    25% {
      transform: translate(30px, -30px);
    }
    50% {
      transform: translate(30px, 30px);
    }
    75% {
      transform: translate(-30px, 30px);
    }
    100% {
      transform: translate(-30px, -30px);
    }
  }
`;

export default function SsoLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useI18n();

  /**
   * @description: 是否在钉钉应用
   * @return: Boolean
   */
  const isDingtalk = (): boolean => {
    // 非专有钉
    const isGdt = navigator.userAgent.toLowerCase().match(/TaurusApp/i) !== null;
    return /dingtalk/.test(navigator.userAgent.toLowerCase()) && !isGdt;
  };

  const handleSsoLogin = useCallback(async (code: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await ssoLogin(code);
      
      if (response.success && response.data) {
        // 保存JWT token
        setToken(response.data.access_token);
        // 设置登录类型为 SSO 登录
        localStorage.setItem('loginType', 'soke_sso');
        // safeToast.success(t('auth.loginSuccess'));
        
        // 使用统一的跳转函数
        handleLoginRedirect({
          searchParams,
          navigate,
          isDingTalk: isDingtalk(),
        });
      } else {
        // 登录失败
        const errorMessage = response.errorMessage || t('auth.ssoLoginFailed');
        setError(errorMessage);
        safeToast.error(errorMessage);
      }
    } catch (error: any) {
      const errorMessage = error.message || t('auth.ssoLoginError');
      setError(errorMessage);
      safeToast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [navigate, searchParams, t]);

  const handleRetry = () => {
    const code = searchParams.get('code');
    if (code) {
      handleSsoLogin(code);
    } else {
      setError(t('auth.missingAuthCode'));
    }
  };

  useEffect(() => {
    const code = searchParams.get('code');
    
    if (!code) {
      setError(t('auth.missingAuthCode'));
      setLoading(false);
      return;
    }

    // 执行SSO登录
    handleSsoLogin(code);
  }, [searchParams, handleSsoLogin, t]);

  return (
    <SsoLoginContainer>
      <LoadingCard>
        <LoaderWrapper>
          <div className="loader">
            <div /> 
            <div />
            <div />
            <div />
          </div>
        </LoaderWrapper>
        
        {loading && (
          <LoadingText>
            {t('auth.ssoLoginVerifying')}
          </LoadingText>
        )}
        
        {error && (
          <>
            <ErrorText>{error}</ErrorText>
            <RetryButton onClick={handleRetry} disabled={loading}>
              {t('common.retry')}
            </RetryButton>
          </>
        )}
      </LoadingCard>
    </SsoLoginContainer>
  );
}